import React, { useEffect, useState, useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@emotion/react';
import { darkTheme, lightTheme } from '../themes';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/utils/formatters';
import useResponsiveChartHeight from '@/components/highcharts/powerAnalyser/hooks/useResponsiveChartHeight';

import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts);

const DurationAnalysisChart = ({
  data,
  loadProfiles,
  xAxisMode = 'percentage',
  isLoading,
}) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();

  const displayNameMap = {
    Peakload: t('duration_analysis.chart.legend.peak'),
    Intermediate: t('duration_analysis.chart.legend.intermediate'),
    Baseload: t('duration_analysis.chart.legend.base'),
  };

  const createLoadProfileSeries = (loadProfiles, durationData) => {
    const profileOrder = {
      Peakload: 0,
      Intermediate: 1,
      Baseload: 2,
    };

    const getProfileColors = (profileName) => {
      const rgb = {
        Peakload: [255, 0, 0],
        Intermediate: [255, 165, 0],
        Baseload: [0, 128, 0],
      }[profileName] || [0, 0, 255];

      const solid = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 1)`;
      const gradient = {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.4)`],
          [1, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.0)`],
        ],
      };

      return { solid, gradient };
    };

    return [...loadProfiles]
      .sort((a, b) => profileOrder[a.Name] - profileOrder[b.Name])
      .map((profile) => {
        const { Name, StartDuration, EndDuration } = profile;
        const seriesData = durationData
          .filter(
            (point) =>
              point.Duration >= StartDuration && point.Duration <= EndDuration,
          )
          .map((point) => [point.Duration, point.Value]);

        const { solid, gradient } = getProfileColors(Name);

        return {
          name: displayNameMap[Name] || Name,
          data: seriesData,
          type: 'area',
          color: solid, // line and marker color
          fillColor: gradient, // background fill
          // marker: {
          //   enabled: true,
          //   fillColor: solid,  // ensures dot is solid
          // },
          custom: {
            profileValue: profile.Value,
            shares: profile.Shares,
            sharesPercentage: profile.SharesPercentage,
            solidColor: solid, // 👈 include for tooltip
          },
          showInLegend: true,
          enableMouseTracking: true,
        };
      });
  };

  // Memoized maxDuration so it doesn't recalculate unnecessarily
  const maxDuration = useMemo(() => {
    return Array.isArray(data) && data.length > 0
      ? Math.max(...data.map(({ Duration }) => Duration))
      : 0;
  }, [data]);

  const chartComponentRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'line',
      zoomType: 'x',
      height: chartHeight,
      spacingLeft: 40, // ✅ Adds left padding
      spacingRight: 40, // ✅ Adds right padding
    },
    title: {
      text: '',
    },
    xAxis: {
      title: { text: t('duration_analysis.chart.x_axis_time') },
      type: 'linear',
      gridLineColor: 'transparent',
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
    },
    yAxis: {
      title: { text: 'kW' },
      //   gridLineColor: 'transparent',
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        let output = `<b>Time: ${this.x}</b><br/>`;
        this.points.forEach((p) => {
          if (p.series.userOptions.custom) {
            const color = p.series.userOptions.custom?.solidColor || p.color;
            const { profileValue, shares, sharesPercentage } =
              p.series.userOptions.custom;
            output += `<span style="color:${color}">\u25CF</span> ${
              p.series.name
            }: <b>${formatNumber(profileValue)} kW</b><br/>`;
            output += `</b><br/>`;
            xAxisMode === 'percentage'
              ? (output += `<b>${t('duration_analysis.distribution_of')} ${t(
                  'duration_analysis.chart.legend.peak',
                )}</b><br/>
                          ${t(
                            'duration_analysis.active_hours',
                          )}: <b>${formatNumber(
                            sharesPercentage?.ActivePeriod * 100,
                          )}% </b><br/>
                          ${t(
                            'duration_analysis.inactive_hours',
                          )}: <b>${formatNumber(
                            sharesPercentage?.InactivePeriod * 100,
                          )}% </b><br/>`)
              : // Inaktive dage: <b>${formatNumber((sharesPercentage.InactiveDays * 100).toFixed(1))}% </b><br/>
                // Inaktive timer: <b>${formatNumber((sharesPercentage.InactiveHours * 100).toFixed(1))}% </b><br/>`
                (output += `${t('duration_analysis.active_hours')}: <b>${
                  shares.ActivePeriod
                }</b><br/>
                             ${t('duration_analysis.inactive_hours')}: <b>${
                               shares.InactivePeriod
                             }</b><br/>`);
            //  Inaktive dage: <b>${shares.InactiveDays}</b><br/>
            //  Inaktive timer: <b>${shares.InactiveHours}</b><br/>`;
          } else {
            output += `<span style="color:${p.color}">\u25CF</span> ${
              p.series.name
            }: <b>${formatNumber(p.y)} kW</b><br/>`;
          }
        });
        return output;
      },
    },
    plotOptions: {
      line: { marker: { enabled: false } }, // Smooth line
    },
    series: [
      {
        name: t('duration_analysis.chart.legend.load'),
        data: [],
        type: 'line',
      },
    ],
    credits: {
      enabled: false,
    },
  });

  // Update data directly on the chart when it changes
  useEffect(() => {
    if (Array.isArray(data)) {
      const formattedData = data.map(({ Duration, Value }) => [
        Duration,
        Value,
      ]);

      const chart = chartComponentRef.current?.chart;
      if (chart) {
        chart.series[0].setData(formattedData, true);
      }
    }
  }, [data]);

  // Update main series and LoadProfile series
  useEffect(() => {
    if (!Array.isArray(data)) return;

    const formattedMainSeries = data.map(({ Duration, Value }) => [
      Duration,
      Value,
    ]);
    const chart = chartComponentRef.current?.chart;

    if (chart) {
      // Update main series
      if (chart.series.length > 0) {
        chart.series[0].setData(formattedMainSeries, false);
      }

      // Remove previous LoadProfile series
      chart.series
        .filter((s) => s.name !== t('duration_analysis.chart.legend.load'))
        .forEach((s) => s.remove(false));

      // Add new LoadProfile series
      if (Array.isArray(loadProfiles)) {
        const profileSeries = createLoadProfileSeries(loadProfiles, data);
        profileSeries.forEach((series) => chart.addSeries(series, false));
      }

      chart.redraw();
    }
  }, [data, loadProfiles]);

  // Update chart theme and axis when theme or xAxisMode changes
  useEffect(() => {
    if (!Array.isArray(data)) return;
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    const chart = chartComponentRef.current?.chart;

    if (chart) {
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: { useUTC: true },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            title: {
              text:
                xAxisMode === 'percentage'
                  ? t('duration_analysis.chart.x_axis_percentage')
                  : t('duration_analysis.chart.x_axis_time'),
            },
            labels: {
              formatter: function () {
                if (xAxisMode === 'percentage' && maxDuration > 0) {
                  const percentage = (this.value / maxDuration) * 100;
                  return `${Math.round(percentage)}%`;
                }
                return this.value;
              },
              style: {
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
              },
            },
            min: 0,
            max: xAxisMode === 'percentage' ? maxDuration : null,
            tickInterval:
              xAxisMode === 'percentage' && maxDuration > 0
                ? maxDuration / 10 // ~10 ticks => intervals like 10%, 20%, ...
                : null,
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
          },
          yAxis: {
            gridLineColor: 'transparent',
            labels: { style: { color: themeOptions.yAxis.labels.style.color } },
          },
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
            },
            formatter: function () {
              let output = `<b>${t('duration_analysis.hour')}: ${
                this.x
              }</b><br/>`;
              this.points.forEach((p) => {
                if (p.series.userOptions.custom) {
                  const color =
                    p.series.userOptions.custom?.solidColor || p.color;
                  const { profileValue, shares, sharesPercentage } =
                    p.series.userOptions.custom;
                  output += `<span style="color:${color}">\u25CF</span> ${
                    p.series.name
                  }: <b>${formatNumber(profileValue)} kW</b><br/>`;
                  output += `</b><br/>`;
                  xAxisMode === 'percentage'
                    ? (output += `<b>${t(
                        'duration_analysis.distribution_of',
                      )} ${p.series.name}</b><br/>
                          ${t(
                            'duration_analysis.active_hours',
                          )}: <b>${formatNumber(
                            sharesPercentage?.ActivePeriod * 100,
                          )}% </b><br/>
                           ${t(
                             'duration_analysis.inactive_hours',
                           )}: <b>${formatNumber(
                             sharesPercentage?.InactivePeriod * 100,
                           )}% </b><br/>`)
                    : (output += ` ${t('duration_analysis.active_hours')}: <b>${
                        shares.ActivePeriod
                      }</b><br/>
                            ${t('duration_analysis.inactive_hours')}: <b>${
                              shares.InactivePeriod
                            }</b><br/>`);
                } else {
                  output += `<span style="color:${p.color}">\u25CF</span> ${
                    p.series.name
                  }: <b>${formatNumber(p.y)} kW</b><br/>`;
                }
              });
              return output;
            },
          },
          series: [
            {
              color: theme.palette.mode === 'dark' ? 'gray' : 'lightgray',
            },
          ],
        },
        false,
      );

      chart.redraw();
    }
  }, [theme, xAxisMode, maxDuration, t]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartComponentRef}
    />
  );
};

export default DurationAnalysisChart;
